import React from 'react';
import ReportShell, { reportStyle, reportButtonStyle, formatReportTimestamp } from '../../../shared/ReportShell';
import { downloadCsv } from '../../../shared/csv';
import { formatCustomerScope, formatProductScope, productScopeTitle, statusLabel } from './PriceTable';
import { useAnchorPriceGroupName } from '../../../shared/useAnchorPriceGroupName';
import { formatDate } from '../../../shared/tenantClock';
import { formatMoney as formatMoneyShared, formatNumber, formatPercent } from '../../../shared/numberFormat';
import TaxBasisLine from '../../../shared/TaxBasisLine';

// TEMPORARY report — a direct cost-vs-manually-entered-price comparison for
// today's Fixed Override breaks, until the full Most-Specific-Wins pricing
// engine (docs/product/modules/Sales/vis_prices-and-pricelists.md)
// exists and can drive a proper margin report off resolved sale prices
// (including Rule-based tiers, which this deliberately skips — a Rule is
// only ever a relative %/$ off with no absolute price to compare a cost
// against). Delete this file once that lands; nothing else depends on it.
//
// Ex-GST only, deliberately — Margin is GST-invariant (GST cancels out of
// both numerator and denominator when Cost and Price carry the same rate),
// so an Inc-GST pair would just be redundant noise on a report whose whole
// point is the Margin figure.
const REPORT_HEADER_HEIGHT = { height: 48, lineHeight: '16px', verticalAlign: 'bottom' };

// 3dp, not the usual 2 — this report is for internal Admin checking, and
// rounding to cents hides exactly the small discrepancies it exists to
// surface (same reasoning as PricePricingPage.js's formatMoney3).
function formatMoney(value) {
  return formatMoneyShared(value, { decimals: 3 });
}

function formatQty(minQuantity) {
  return formatNumber(minQuantity, { decimals: 0 });
}

// minQuantity is stored in the product's Base UoM (Pieces) — converted
// back to the adjustment's own selling UoM here (rounded up, since a
// partial Pack can't reach a threshold), same conversion
// PriceBreakReportView's own UoM Qty column uses, duplicated here rather
// than imported since this file is meant to be deletable on its own
// without touching the permanent report.
function formatUomQty(minQuantity, item) {
  if (minQuantity === null || minQuantity === undefined) return '';
  if (!item.uom_conversion_rate) return formatQty(minQuantity);
  return formatQty(Math.ceil(Number(minQuantity) / item.uom_conversion_rate));
}

// Min Qty in the Base UoM (e.g. PCS) — already what minQuantity is stored
// as, no conversion needed. Same gating as PriceBreakReportView's own Base
// Qty column, duplicated here for the same reason as formatUomQty above.
function formatBaseQty(minQuantity, item) {
  if (minQuantity === null || minQuantity === undefined || !item.uom_conversion_rate) return '—';
  const baseName = item.product?.base_uom?.code;
  const qty = formatQty(minQuantity);
  return baseName ? `${qty} ${baseName}` : qty;
}

// Product's cost, ex-GST, converted to AUD when Supplier-linked — mirrors
// productReportShared.js's formatCost. cost_price and cost_price_foreign are
// mutually exclusive on the Product record (productController.js), so this
// never double-counts. Already per-Base-UoM-unit as stored — no conversion
// needed, unlike Price below, since there's no per-UoM cost schedule.
function resolveProductCost(product) {
  if (!product) return null;
  if (product.supplier && product.cost_price_foreign !== null && product.cost_price_foreign !== undefined) {
    return Number(product.cost_price_foreign) * Number(product.supplier.exchange_rate);
  }
  if (product.cost_price === null || product.cost_price === undefined) return null;
  return Number(product.cost_price);
}

// Retail's own Price is margin reporting's benchmark regardless of how a
// row's own Price was set (vis_prices-and-pricelists.md) — a Rule row's
// effective price at a given tier is computable now that Retail's figure
// is available (retail adjusted by the tier's Type/Amount), closing the
// gap that used to exclude Rule rows outright ("no absolute price to
// compare a cost against"). Mirrors priceResolution.js's applyPrice
// arithmetic — duplicated here (not imported) since this file is meant to
// stay deletable on its own, without a backend-service dependency.
function applyRuleTier(retailPrice, rule, tier) {
  if (retailPrice === null || retailPrice === undefined) return null;
  const amount = Number(tier.amount);
  return rule.type === 'amount_off' ? retailPrice - amount : retailPrice * (1 - amount / 100);
}

// One row per priced tier/break — a Fixed Override break with a real
// entered (non-zero) price, or a Rule tier once Retail's Price makes its
// effective price computable. Cost-based Margin only ever applies to the
// Fixed Override case (a Rule is still only ever a relative %/$ off, so
// there's nothing of its own to divide Cost into beyond the effective
// price already shown) — everything else is out of scope for this stopgap
// report: a Product Price Group/All Products scope (no single Product to
// cost or convert UoM against) and a Product with no cost recorded.
function flattenPricedRows(items) {
  const rows = [];
  items.forEach((item) => {
    if (!item.product || !item.uom_conversion_rate) return;
    const cost = resolveProductCost(item.product);
    const retailPrice = item.retail_price === null || item.retail_price === undefined ? null : Number(item.retail_price);

    if (item.rule) {
      (item.rule.tiers || []).forEach((tier) => {
        const exPrice = applyRuleTier(retailPrice, item.rule, tier);
        rows.push({ item, minQuantity: tier.min_quantity, exPrice, cost: null, retailPrice, isRule: true });
      });
      return;
    }

    if (item.needs_price || cost === null) return;
    (item.breaks || []).forEach((brk) => {
      const exPrice = Number(brk.price);
      if (exPrice === 0) return;
      rows.push({ item, minQuantity: brk.min_quantity, exPrice, cost, retailPrice, isRule: false });
    });
  });
  return rows;
}

// The whole point of this report: Cost, Price and Retail Price all
// expressed per one Base UoM unit (e.g. per Piece), regardless of which
// UoM this break's price was actually entered in (e.g. a Pack of 10) — so
// Margin is comparable across a family of products or across a product's
// own different UoM tiers without mentally converting each one. Price is
// the tier/break's own effective price divided by the conversion rate
// (same math PriceBreakReportView's Base Price columns already use), and
// so is Retail Price — the backend resolves it for the row's own UoM (Base
// Price x that UoM's quantity, retailPriceService.js), so dividing it back
// down gives the Base Price. Cost needs no conversion since it's already
// stored per Base UoM. Margin comes out identical whether computed from these
// per-piece figures or from the raw break-level ones — the conversion
// rate cancels out of the ratio — so there's only one Margin number, not a
// per-UoM and a per-piece version. null when there's nothing to compute
// from (a Rule row has no Cost benchmark; either row's Price/Retail Price
// can themselves be null — an unresolved Rule tier, or Retail's own Price
// missing).
function perBaseUomFigures(row) {
  const pricePerBase = row.exPrice === null || row.exPrice === undefined ? null : row.exPrice / row.item.uom_conversion_rate;
  const margin = row.cost === null || pricePerBase === null ? null : ((pricePerBase - row.cost) / pricePerBase) * 100;
  const retailPerBase = row.retailPrice === null || row.retailPrice === undefined ? null : row.retailPrice / row.item.uom_conversion_rate;
  return { costPerBase: row.cost, pricePerBase, retailPerBase, margin };
}

function formatPerBaseUom(value, item) {
  if (value === null || value === undefined) return '—';
  const baseName = item.product?.base_uom?.code;
  return baseName ? `${formatMoney(value)}/${baseName}` : formatMoney(value);
}

function formatMargin(margin) {
  return formatPercent(margin) || '—';
}

function formatCustomerScopeFull(item) {
  if (item.customer) return item.customer.name;
  if (item.customer_price_group) return item.customer_price_group.name;
  return 'All Customers';
}

function groupByCustomer(flatRows) {
  const sections = [];
  flatRows.forEach((row) => {
    const customerLabel = formatCustomerScopeFull(row.item);
    const current = sections[sections.length - 1];
    if (current && current.customerLabel === customerLabel) {
      current.rows.push(row);
    } else {
      sections.push({ customerLabel, rows: [row] });
    }
  });
  return sections;
}

function withProductSuppression(rows) {
  let prevProductLabel = null;
  return rows.map((row) => {
    const productLabel = formatProductScope(row.item);
    const showProduct = productLabel !== prevProductLabel;
    prevProductLabel = productLabel;
    return { ...row, productLabel, showProduct };
  });
}

function buildCsvRows(items, anchorName) {
  return flattenPricedRows(items).map((row) => {
    const { item, minQuantity } = row;
    const { costPerBase, pricePerBase, retailPerBase, margin } = perBaseUomFigures(row);
    return {
      Customer: formatCustomerScope(item),
      SKU: formatProductScope(item),
      UoM: item.uom ? item.uom.code : '(All UoMs)',
      'UoM Qty': formatUomQty(minQuantity, item),
      'Base Qty': formatBaseQty(minQuantity, item),
      [`${anchorName} Price (ex GST)`]: retailPerBase === null ? '' : retailPerBase.toFixed(3),
      'Cost (ex GST)': costPerBase === null ? '' : costPerBase.toFixed(3),
      'Price (ex GST)': pricePerBase === null ? '' : pricePerBase.toFixed(3),
      'Margin %': margin === null ? '' : formatMargin(margin),
      'Effective From': item.start_date || '',
      'Effective To': item.end_date || '',
      Status: statusLabel(item.status),
    };
  });
}

function MarginReportView({ items, loading, filterSummary, onClose }) {
  const anchorName = useAnchorPriceGroupName();
  const sections = loading
    ? []
    : groupByCustomer(flattenPricedRows(items)).map((section) => ({ ...section, rows: withProductSuppression(section.rows) }));
  const totalRows = sections.reduce((sum, section) => sum + section.rows.length, 0);
  const isEmpty = !loading && totalRows === 0;

  return (
    <ReportShell
      title="Margin Report"
      subtitle={(
        <>
          <div>{`${formatReportTimestamp()} · ${totalRows} priced row${totalRows === 1 ? '' : 's'}`}</div>
          <div>Temporary report — un-priced items aren&apos;t shown. Margin % only applies to Fixed Override rows (a Rule has no Cost benchmark of its own); {anchorName} Price is shown for every row regardless.</div>
          <div>{anchorName} Price, Cost and Price are shown per Base UoM unit (e.g. per Piece), regardless of this row's own UoM, so Margin is directly comparable across UoMs and products.</div>
          {filterSummary && <div>Filtered by: {filterSummary}</div>}
        </>
      )}
      onClose={onClose}
      printDisabled={loading || isEmpty}
      actions={(
        <button
          type="button"
          onClick={() => downloadCsv('margin-report.csv', buildCsvRows(items, anchorName))}
          disabled={loading || isEmpty}
          style={reportButtonStyle(loading || isEmpty)}
        >
          Download CSV
        </button>
      )}
    >
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>Loading...</div>
      ) : isEmpty ? (
        <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>No priced rows match the current filters.</div>
      ) : (
        sections.map((section, sIdx) => (
          <div
            key={`${section.customerLabel}-${sIdx}`}
            style={sIdx > 0 ? { breakBefore: 'page', pageBreakBefore: 'always', marginTop: 24 } : undefined}
          >
            <h3 style={{ margin: '0 0 6px', fontSize: 15, borderBottom: reportStyle.tableHeaderBorder, paddingBottom: 6 }}>{section.customerLabel}</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: reportStyle.tableFontSize, marginBottom: 20 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: reportStyle.tableHeaderBorder }}>
                  <th style={{ padding: '6px 8px 6px 0', ...REPORT_HEADER_HEIGHT }}>SKU</th>
                  <th style={{ padding: '6px 8px 6px 0', ...REPORT_HEADER_HEIGHT }}>UoM</th>
                  <th style={{ padding: '6px 8px 6px 0', textAlign: 'right', ...REPORT_HEADER_HEIGHT }}>UoM<br />Qty</th>
                  <th style={{ padding: '6px 8px 6px 0', textAlign: 'right', ...REPORT_HEADER_HEIGHT }}>Base<br />Qty</th>
                  <th style={{ padding: '6px 8px 6px 0', textAlign: 'right', ...REPORT_HEADER_HEIGHT }}>{anchorName}<TaxBasisLine>ex GST</TaxBasisLine></th>
                  <th style={{ padding: '6px 8px 6px 0', textAlign: 'right', ...REPORT_HEADER_HEIGHT }}>Cost<TaxBasisLine>ex GST</TaxBasisLine></th>
                  <th style={{ padding: '6px 8px 6px 0', textAlign: 'right', ...REPORT_HEADER_HEIGHT }}>Price<TaxBasisLine>ex GST</TaxBasisLine></th>
                  <th style={{ padding: '6px 20px 6px 0', textAlign: 'right', ...REPORT_HEADER_HEIGHT }}>Margin<br />%</th>
                  <th style={{ padding: '6px 8px 6px 0', ...REPORT_HEADER_HEIGHT }}>Effective</th>
                  <th style={{ padding: '6px 8px 6px 0', textAlign: 'center', ...REPORT_HEADER_HEIGHT }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {section.rows.map((row, idx) => {
                  const { item, minQuantity, productLabel, showProduct } = row;
                  const { costPerBase, pricePerBase, retailPerBase, margin } = perBaseUomFigures(row);
                  return (
                    <tr key={`${item.id}-${idx}`} style={{ borderBottom: '1px solid #ddd', breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                      <td style={{ padding: '6px 8px 6px 0' }} title={productScopeTitle(item)}>{showProduct ? productLabel : ''}</td>
                      <td style={{ padding: '6px 8px 6px 0' }}>{item.uom ? item.uom.code : '(All UoMs)'}</td>
                      <td style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>{formatUomQty(minQuantity, item)}</td>
                      <td style={{ padding: '6px 8px 6px 0', textAlign: 'right', color: '#888' }}>{formatBaseQty(minQuantity, item)}</td>
                      <td style={{ padding: '6px 8px 6px 0', textAlign: 'right', color: '#888' }}>{formatPerBaseUom(retailPerBase, item)}</td>
                      <td style={{ padding: '6px 8px 6px 0', textAlign: 'right', color: '#888' }}>{formatPerBaseUom(costPerBase, item)}</td>
                      <td style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>{formatPerBaseUom(pricePerBase, item)}</td>
                      <td style={{ padding: '6px 20px 6px 0', textAlign: 'right', fontWeight: 600 }}>{formatMargin(margin)}</td>
                      <td style={{ padding: '6px 8px 6px 0' }}>{formatDate(item.start_date) || '(any)'} – {formatDate(item.end_date) || '(any)'}</td>
                      <td style={{ padding: '6px 8px 6px 0', textAlign: 'center' }}>
                        {statusLabel(item.status)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))
      )}
    </ReportShell>
  );
}

export default MarginReportView;
